/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IChatConversation,
  IChatMessage,
  IChatParticipant,
  SendStatus,
} from '@dataTypes/chat.type'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  addChat,
  addConversation,
  ChatResponseData,
  ConversationResponseData,
  deleteConversationById,
  getAllChatConversation,
  getBulkPresignedUrl,
  getLinkPreview,
  sendSms,
  updateChatById,
  updateConversationById,
  updateMultipleChatMessages,
  updateMultipleConversation,
} from '@services/api/chat.api'
import { logger } from '@utils/safeLogger'
import { sortConversationByDate } from '@utils/chat.util'
import {
  resolveSmsClinicNumberId,
  resolveSmsSenderPhone,
} from '@utils/messaging-settings.util'
import { SET_LAST_ACTIVE_CONVERSATION } from '@store/reducers/messageCenter.reducer'
import { RootState } from '@store/index'

// Map initial Twilio message status to chat send_status enum
const mapTwilioToSendStatus = (status?: string): SendStatus => {
  switch ((status || '').trim().toLowerCase()) {
    case 'sent':
      return SendStatus.sent
    case 'delivered':
      return SendStatus.delivered
    case 'undelivered':
      return SendStatus.undelivered
    case 'failed':
      return SendStatus.failed
    case 'queued':
    case 'accepted':
    case 'scheduled':
    case 'sending':
      return SendStatus.queued
    default:
      return SendStatus.queued
  }
}

export const fetchAllConversations = createAsyncThunk<
  IChatConversation[],
  void,
  { rejectValue: string }
>('api/chat/fetch', async (_, { rejectWithValue }) => {
  const response = await getAllChatConversation('External')

  if (response.statusCode !== 200) {
    logger.error('Failed to fetch conversations', {
      status: response.statusCode,
      message: response.message,
    })
    return rejectWithValue(response.message)
  }

  if (
    response.data &&
    'conversations' in response.data &&
    Array.isArray(response.data.conversations)
  ) {
    const _conversations = response.data.conversations

    if (_conversations.length < 1) {
      return []
    }

    const presignedResponse = await getBulkPresignedUrl(_conversations)

    if (presignedResponse.statusCode !== 200) {
      logger.error('Failed to get presigned URLs', {
        message: presignedResponse.message,
      })
      return rejectWithValue(presignedResponse.message)
    }

    if (!presignedResponse.data?.conversations) {
      logger.error('No conversations in presigned response')
      return rejectWithValue('No conversations in presigned response')
    }

    const conversations = presignedResponse.data
      .conversations as IChatConversation[]

    const sortedConversations = sortConversationByDate(conversations)

    return sortedConversations
  }

  logger.error('Invalid chat response format')
  return rejectWithValue('Conversations not found in the response')
})

export const createChat = createAsyncThunk<
  void,
  IChatMessage,
  {
    rejectValue: string
  }
>('api/chat/add/message', async (message, { dispatch, rejectWithValue }) => {
  try {
    const res = await addChat(message)

    if (res.statusCode !== 201) {
      return rejectWithValue(res.message || 'Failed to fetch appointments')
    }

    dispatch(fetchAllConversations())
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create appointment')
  }
})

export const createSms = createAsyncThunk<
  IChatConversation[],
  IChatMessage,
  { rejectValue: string; state: RootState }
>('api/chat/add/sms', async (message, { getState, rejectWithValue }) => {
  const state = getState()
  const selectedClinic = state.clinic.selectedClinic
  const sid = selectedClinic?.twilio_account_sid
  const token = selectedClinic?.twilio_auth_token
  const clinicId = selectedClinic?.clinic_id
  const settings = state.messagingSettings.current
  const clinicNumbers = selectedClinic?.clinic_number ?? []
  const conversation =
    state.chat.selectedMessage?.chat_conversation_id ===
    message.chat_conversation_id
      ? state.chat.selectedMessage
      : state.chat.data.find(
          (item) => item.chat_conversation_id === message.chat_conversation_id,
        )
  const clinicNumberId = resolveSmsClinicNumberId(
    conversation,
    settings,
    clinicNumbers,
  )
  const senderPhone = resolveSmsSenderPhone(
    conversation,
    clinicNumbers,
    settings,
  )
  if (settings?.enable_sms === false) {
    return rejectWithValue(
      'SMS messaging is disabled. Enable it in Settings → Communications → Messaging.',
    )
  }
  if (!senderPhone) {
    return rejectWithValue(
      'No SMS clinic number is configured for this conversation.',
    )
  }
  const outboundMessage: IChatMessage = {
    ...message,
    sender_id: senderPhone,
  }
  try {
    let twilioSid: string | undefined
    let sendStatus: SendStatus = SendStatus.queued

    if (sid && token) {
      try {
        const smsResponse = await sendSms(outboundMessage, sid, token, {
          clinic_id: clinicId,
          clinic_number_id: clinicNumberId,
        })
        const sms = (
          smsResponse?.data as {
            sms?: { sid?: string; status?: string; errorCode?: number }
          }
        )?.sms
        if (smsResponse.statusCode >= 400 || sms?.errorCode) {
          sendStatus = SendStatus.failed
        } else {
          twilioSid = sms?.sid
          sendStatus = mapTwilioToSendStatus(sms?.status)
        }
      } catch {
        sendStatus = SendStatus.failed
      }
    } else {
      sendStatus = SendStatus.failed
    }

    const messageToPersist: IChatMessage = {
      ...outboundMessage,
      ...(twilioSid ? { external_id: twilioSid } : {}),
      send_status: sendStatus,
    }

    const chat = await addChat(messageToPersist)

    if (chat.statusCode !== 201) {
      return rejectWithValue(chat.message)
    }

    return chat.data.conversations as IChatConversation[]
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create SMS')
  }
})

export const createConversation = createAsyncThunk<
  ConversationResponseData,
  { conversation_data: IChatConversation; members_data: IChatParticipant[] },
  { rejectValue: string }
>('api/chat/add/conversation', async (params, { rejectWithValue }) => {
  try {
    const response = await addConversation(
      params.conversation_data,
      params.members_data,
    )
    return response.data as ConversationResponseData
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create conversation')
  }
})

export const updateChat = createAsyncThunk<
  ChatResponseData,
  { chat_message_id: string; data: Partial<IChatMessage> },
  { rejectValue: string }
>('api/chat/update/chat', async (params, { rejectWithValue }) => {
  try {
    const updatedChat = await updateChatById(
      params.chat_message_id,
      params.data,
    )
    return updatedChat.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update chat')
  }
})

export const updateSelectedMessage = createAsyncThunk<
  { id: string | null },
  undefined | { id: string },
  { rejectValue: string }
>(
  'api/chat/update/selectedMessage',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      if (params) dispatch(SET_LAST_ACTIVE_CONVERSATION(params.id))
      return {
        id: params?.id || null,
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update conversation')
    }
  },
)

export const updateConversation = createAsyncThunk<
  IChatConversation[],
  { id: string; data: Partial<IChatConversation> },
  { rejectValue: string }
>('api/chat/update/conversation', async (params, { rejectWithValue }) => {
  try {
    const response = await updateConversationById(params.id, params.data)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    return response.data.conversations as IChatConversation[]
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update conversation')
  }
})

export const bulkUpdateConversation = createAsyncThunk<
  { ids: string[]; data: Partial<IChatConversation> },
  { ids: string[]; data: Partial<IChatConversation> },
  { rejectValue: string }
>('api/chat/update/conversation/bulk', async (params, { rejectWithValue }) => {
  try {
    const response = await updateMultipleConversation(params.ids, params.data)
    if (response.statusCode !== 201) {
      return rejectWithValue(response.message)
    }

    return params
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update conversation')
  }
})

export const bulkUpdateChatMessages = createAsyncThunk<
  { ids: string[]; data: Partial<IChatMessage> },
  { ids: string[]; data: Partial<IChatMessage> },
  { rejectValue: string }
>('api/chat/update/message/bulk', async (params, { rejectWithValue }) => {
  try {
    const response = await updateMultipleChatMessages(params.ids, params.data)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    return params
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update chat messages')
  }
})

export const deleteConversation = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/chat/delete/conversation', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteConversationById(id)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return id
  } catch (error) {
    return rejectWithValue('Failed to delete conversation')
  }
})

export const fetchLinkPreview = createAsyncThunk<
  { url: string; image: string },
  string,
  { rejectValue: string }
>('api/chat/get/link-preview', async (url, { rejectWithValue }) => {
  try {
    const response = await getLinkPreview(url)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data.linkPreview
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to get link preview',
    )
  }
})
